/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/utils/formatEventTime.ts
 *
 * Rôle :
 *   LOT-H (M7/H) : Formatage FR de l'horaire de l'événement pour le Footer,
 *   DÉRIVÉ de la source autoritaire `tne_events` (via useEventConfig :
 *   `starts_at` / `ends_at`). Évite de figer « 8 h 30 à 13 h » en dur :
 *   l'heure de fin affichée suit TOUJOURS la valeur réelle de la base
 *   (réglée au back-office /admin/event-config), la décision 12 h 00 vs
 *   13 h 00 se tranche donc côté donnée, jamais dans le code.
 *
 *   Le réglage de contenu `content.footer.event_time` (LOT-H) reste un
 *   OVERRIDE optionnel : si l'admin le remplit, il prime sur la dérivation.
 *
 * Pourquoi un parsing par regex et pas `new Date()` :
 *   `starts_at`/`ends_at` arrivent au format local Paris
 *   « YYYY-MM-DD HH:MM:SS » (ou ISO « …THH:MM… »), SANS fuseau explicite.
 *   `new Date('2026-10-09 13:00:00')` est interprété différemment selon
 *   le moteur (UTC vs local) → on extrait l'heure littérale telle quelle,
 *   100 % déterministe, sans dépendance ni décalage de fuseau.
 *
 * Réutilisable : 100 %, toute app DRANE affichant un créneau FR.
 *
 * Voir : hooks/useEventConfig.ts (source des dates), components/layout/Footer.tsx
 *        (consommateur), CLAUDE-NOLAS.md §3 LOT-H.
 *
 * @author Mikael
 * @since  2026-06-20 (finition LOT-H : Footer horaire dérivé)
 */

/**
 * Extrait l'heure « H h MM » d'un horodatage local (« YYYY-MM-DD HH:MM:SS »
 * ou ISO). Style typographique FR : heure SANS zéro de tête, séparateur
 * « h » entouré d'espaces, minutes sur 2 chiffres (« 8 h 30 », « 13 h 00 »).
 *
 * @param datetime Horodatage local (ou null/undefined).
 * @returns « H h MM » ou `null` si non parsable.
 */
export function formatFrTime(datetime: string | null | undefined): string | null {
  if (typeof datetime !== 'string') return null;
  // Première occurrence HH:MM précédée d'un espace ou d'un « T » (séparateur
  // date/heure ISO ou SQL). Ignore volontairement les secondes / le fuseau.
  const match = /[T ](\d{2}):(\d{2})/.exec(datetime);
  if (!match) return null;
  const hour = Number.parseInt(match[1]!, 10);
  const minute = match[2]!;
  if (Number.isNaN(hour) || hour > 23) return null;
  // Heure sans zéro de tête (« 8 h 30 ») + minutes sur 2 chiffres.
  return `${hour} h ${minute}`;
}

/**
 * Construit le créneau FR « 8 h 30 à 13 h » à partir des bornes de
 * l'événement. Tolérant : si une seule borne est connue, renvoie celle-ci ;
 * si aucune n'est parsable, renvoie `null` (le Footer masque alors l'horaire).
 *
 * @param startsAt Début de l'événement (autoritaire).
 * @param endsAt   Fin de l'événement (autoritaire, « heure de fin »).
 * @returns « 8 h 30 à 13 h », une seule borne, ou `null`.
 */
export function formatEventWindowFr(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string | null {
  const start = formatFrTime(startsAt);
  const end = formatFrTime(endsAt);
  // 2026-10-06 (Mika) : « 8 h 30 à 12 h » ; le code ne compose plus de
  // tiret long, et une heure ronde s'écrit sans « 00 » dans la plage.
  // Mikael - chef de projet - 2026-10-06
  if (start && end) return `${sansMinutesNulles(start)} à ${sansMinutesNulles(end)}`;
  return start ?? end ?? null;
}

/** « 12 h 00 » devient « 12 h » ; « 8 h 30 » reste tel quel. */
function sansMinutesNulles(heure: string): string {
  return heure.endsWith(' h 00') ? heure.slice(0, -3) : heure;
}
